
export type Plan = 'free' | 'founder' | 'pro';

export type PlanConfig = {
  id: Plan;
  name: string;
  monthlyPriceLabel: string;
  generationLimit: number | null; // null for unlimited
  activeProjectLimit: number | null; // null for unlimited
  canDeploy: boolean;
  requiresBrandingBadge: boolean;
  speedTier: 'standard' | 'priority';
  advancedFeatures: boolean;
  recommended?: boolean;
};

export type UsageState = {
  generationsUsed: number;
  activeProjects: number;
  hasAttemptedDeploy: boolean;
};

export const PLANS: Record<Plan, PlanConfig> = {
  free: {
    id: 'free',
    name: 'Free',
    monthlyPriceLabel: '$0',
    generationLimit: 5,
    activeProjectLimit: 1,
    canDeploy: false,
    requiresBrandingBadge: true,
    speedTier: 'standard',
    advancedFeatures: false,
  },
  founder: {
    id: 'founder',
    name: 'Founder',
    monthlyPriceLabel: '$29',
    generationLimit: null,
    activeProjectLimit: 3,
    canDeploy: true,
    requiresBrandingBadge: true,
    speedTier: 'standard',
    advancedFeatures: false,
    recommended: true,
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    monthlyPriceLabel: '$99',
    generationLimit: null,
    activeProjectLimit: null,
    canDeploy: true,
    requiresBrandingBadge: false,
    speedTier: 'priority',
    advancedFeatures: true,
  },
};

export type UIIntent = 'generate' | 'edit' | 'deploy' | 'create_project';
export type UpgradeTriggerStore = 'generation_limit' | 'deploy_block' | 'project_limit' | null;

export function getPlanConfig(planId: string | undefined): PlanConfig {
  const id = (planId || 'free') as Plan;
  return PLANS[id] || PLANS.free;
}

export function canGenerate(plan: PlanConfig, usage: UsageState): boolean {
  if (plan.generationLimit === null) return true;
  return usage.generationsUsed < plan.generationLimit;
}

export function canCreateProject(plan: PlanConfig, usage: UsageState): boolean {
  if (plan.activeProjectLimit === null) return true;
  return usage.activeProjects < plan.activeProjectLimit;
}

export function canDeploy(plan: PlanConfig): boolean {
  return plan.canDeploy;
}

export function getUpgradeTrigger(planId: Plan, usage: UsageState, intent: UIIntent): UpgradeTriggerStore {
  const plan = PLANS[planId];
  
  if (intent === 'generate' && !canGenerate(plan, usage)) {
    return 'generation_limit';
  }
  
  if (intent === 'create_project' && !canCreateProject(plan, usage)) {
    return 'project_limit';
  }
  
  if (intent === 'deploy' && !canDeploy(plan)) {
    return 'deploy_block';
  }
  
  return null;
}
